import { useState, useCallback, useRef } from 'react';

interface CustomRoom {
  id: string;
  name: string;
  count: number;
}

interface LayoutConfig {
  bedrooms: number;
  bathrooms: number;
  livingRooms: number;
  customRooms: CustomRoom[];
  area: number;
  unit: string;
  kitchenType: string;
  extras: string[];
}

interface SavedLayout {
  id: string;
  name: string;
  config: LayoutConfig;
  cost: number;
  costMin: number;
  costMax: number;
  tier: string;
}

interface CompareLayoutsProps {
  darkMode: boolean;
  currentConfig: LayoutConfig;
  selectedStyle: string;
  locationMultiplier?: number;
  onPinToggle?: () => void;
}

const EXTRA_COSTS: Record<string, number> = {
  Pool: 40000, Garage: 25000, Gym: 20000, Basement: 30000,
  Balcony: 8000, Patio: 12000, 'Home Office': 15000, 'Wine Cellar': 18000,
  'Home Theater': 35000, Sauna: 10000, Library: 12000, 'Game Room': 15000,
  Workshop: 20000, Mudroom: 5000, 'Laundry Room': 8000, 'Walk-in Closet': 6000,
  Sunroom: 22000, Greenhouse: 16000,
};

const STYLE_COSTS: Record<string, number> = {
  Minimalist: 120, Ranch: 120, Colonial: 175, Craftsman: 175, Farmhouse: 175,
  'Cape Cod': 175, Victorian: 250, Mediterranean: 250, Tudor: 250,
  Contemporary: 200, Industrial: 200, Scandinavian: 200,
  'Tiny Home': 150, Container: 140, 'A-Frame': 160, Dome: 180,
};

const TIER_COLORS: Record<string, string> = {
  Budget: 'text-emerald-500',
  'Mid-Range': 'text-teal-500',
  Premium: 'text-violet-500',
  Luxury: 'text-amber-500',
};

const TIER_BG: Record<string, string> = {
  Budget: 'bg-emerald-500',
  'Mid-Range': 'bg-teal-500',
  Premium: 'bg-violet-500',
  Luxury: 'bg-amber-500',
};

function calcCost(config: LayoutConfig, style: string, locationMultiplier = 1) {
  const area = config.unit === 'sqm' ? config.area * 10.764 : config.area;
  const cpp = STYLE_COSTS[style] || 175;
  let base = Math.round(area * cpp * locationMultiplier);
  const livingRooms = config.livingRooms || 1;
  base = Math.round(base * (1 + (livingRooms - 1) * 0.08));
  let extras = 0;
  config.extras.forEach(e => { extras += Math.round((EXTRA_COSTS[e] || 10000) * locationMultiplier); });
  const customCost = (config.customRooms || []).reduce((s, r) => s + Math.round((EXTRA_COSTS[r.name] || 15000) * r.count * locationMultiplier), 0);
  const stylePremium = style ? Math.round(base * 0.08) : 0;
  const total = base + extras + customCost + stylePremium;
  const tier = cpp <= 140 ? 'Budget' : cpp <= 190 ? 'Mid-Range' : cpp <= 230 ? 'Premium' : 'Luxury';
  return { total, totalMin: Math.round(total * 0.85), totalMax: Math.round(total * 1.15), tier };
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
}

function roomSummary(c: LayoutConfig) {
  const parts = [`${c.livingRooms || 1} Living`, `${c.bedrooms} Bed`, `${c.bathrooms} Bath`];
  (c.customRooms || []).forEach(r => parts.push(`${r.count > 1 ? r.count + '× ' : ''}${r.name}`));
  return parts.join(' · ');
}

export default function CompareLayouts({ darkMode, currentConfig, selectedStyle, locationMultiplier = 1, onPinToggle }: CompareLayoutsProps) {
  const [layouts, setLayouts] = useState<SavedLayout[]>([]);
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [toast, setToast] = useState('');

  // Inline rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const handleSaveLayout = () => {
    const name = nameInput.trim();
    if (!name) { setNameError('Enter a layout name'); return; }
    if (layouts.length >= 3) { setNameError('Max 3 layouts to compare'); return; }
    if (layouts.some(l => l.name.toLowerCase() === name.toLowerCase())) {
      setNameError('Name already used'); return;
    }
    const { total, totalMin, totalMax, tier } = calcCost(currentConfig, selectedStyle, locationMultiplier);
    const newLayout: SavedLayout = {
      id: Date.now().toString(),
      name,
      config: { ...currentConfig, customRooms: (currentConfig.customRooms || []).map(r => ({ ...r })) },
      cost: total,
      costMin: totalMin,
      costMax: totalMax,
      tier,
    };
    setLayouts(prev => [...prev, newLayout]);
    setNameInput('');
    setNameError('');
    showToast(`"${name}" added to comparison`);
  };

  const handleRemoveLayout = (id: string) => {
    setLayouts(prev => prev.filter(l => l.id !== id));
    if (renamingId === id) setRenamingId(null);
  };

  const handleClearAll = () => {
    setLayouts([]);
    setRenamingId(null);
    showToast('Comparison cleared');
  };

  // ── Inline rename handlers ──
  const startRename = (layout: SavedLayout) => {
    setRenamingId(layout.id);
    setRenameValue(layout.name);
    setRenameError('');
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  const commitRename = (id: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenameError('Name cannot be empty'); return; }
    const duplicate = layouts.some(l => l.id !== id && l.name.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) { setRenameError('Name already used'); return; }
    setLayouts(prev => prev.map(l => l.id === id ? { ...l, name: trimmed } : l));
    setRenamingId(null);
    setRenameError('');
    showToast(`Renamed to "${trimmed}"`);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
    setRenameError('');
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(id); }
    if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
  };

  const cheapest = layouts.length > 0 ? layouts.reduce((a, b) => a.cost < b.cost ? a : b) : null;
  const mostRooms = layouts.length > 0 ? layouts.reduce((a, b) => {
    const aR = (a.config.livingRooms || 1) + a.config.bedrooms + a.config.bathrooms + (a.config.customRooms || []).reduce((s, r) => s + r.count, 0);
    const bR = (b.config.livingRooms || 1) + b.config.bedrooms + b.config.bathrooms + (b.config.customRooms || []).reduce((s, r) => s + r.count, 0);
    return aR > bR ? a : b;
  }) : null;

  const maxCost = layouts.length > 0 ? Math.max(...layouts.map(l => l.cost)) : 1;

  return (
    <div className={`rounded-xl border p-4 space-y-4 ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${darkMode ? 'bg-teal-900/40' : 'bg-teal-100'}`}>
            <i className={`ri-scales-3-line text-lg ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
          </div>
          <div>
            <h4 className={`text-sm font-bold ${text}`}>Compare Layouts</h4>
            <p className={`text-xs ${subtext}`}>Save up to 3 configs and compare costs side-by-side</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onPinToggle && (
            <button
              onClick={onPinToggle}
              title="Pin as floating overlay"
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${darkMode ? 'bg-teal-900/30 text-teal-400 hover:bg-teal-800/40' : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'}`}
            >
              <i className="ri-pushpin-line text-xs"></i>Pin
            </button>
          )}
          {layouts.length > 1 && (
            <button
              onClick={handleClearAll}
              className={`text-xs font-medium cursor-pointer ${darkMode ? 'text-gray-400 hover:text-rose-400' : 'text-gray-400 hover:text-rose-500'}`}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Add current layout */}
      <div className={`rounded-xl p-3 border ${darkMode ? 'bg-teal-900/20 border-teal-700/40' : 'bg-teal-50 border-teal-200'}`}>
        <p className={`text-xs font-semibold mb-1 ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>
          <i className="ri-map-pin-line mr-1"></i>Current Config
        </p>
        <p className={`text-xs mb-3 ${subtext}`}>{roomSummary(currentConfig)} · {currentConfig.area} {currentConfig.unit}</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={nameInput}
            onChange={(e) => { setNameInput(e.target.value); setNameError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLayout(); }}
            placeholder={`Name this layout… (${layouts.length}/3)`}
            maxLength={30}
            className={`flex-1 px-3 py-2 rounded-lg border text-xs transition-colors ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            } focus:ring-2 focus:ring-teal-400 focus:border-transparent`}
          />
          <button
            onClick={handleSaveLayout}
            disabled={layouts.length >= 3 || !nameInput.trim()}
            className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              layouts.length >= 3 || !nameInput.trim()
                ? darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}
          >
            <i className="ri-add-line mr-1"></i>Add
          </button>
        </div>
        {nameError && (
          <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>{nameError}
          </p>
        )}
      </div>

      {/* Comparison cards */}
      {layouts.length === 0 ? (
        <div className={`rounded-xl border-2 border-dashed p-6 text-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <i className={`ri-scales-3-line text-xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
          </div>
          <p className={`text-xs font-semibold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No layouts saved yet</p>
          <p className={`text-xs mt-1 ${subtext}`}>Configure rooms above, then add a layout to compare</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Winner badges */}
          {layouts.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {cheapest && (
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                  <i className="ri-money-dollar-circle-line"></i>
                  Most affordable: {cheapest.name}
                </span>
              )}
              {mostRooms && mostRooms.id !== cheapest?.id && (
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${darkMode ? 'bg-violet-900/30 text-violet-400' : 'bg-violet-50 text-violet-700 border border-violet-200'}`}>
                  <i className="ri-home-gear-line"></i>
                  Most rooms: {mostRooms.name}
                </span>
              )}
            </div>
          )}

          {layouts.map((layout) => {
            const isCheapest = cheapest?.id === layout.id && layouts.length > 1;
            const totalRooms = (layout.config.livingRooms || 1) + layout.config.bedrooms + layout.config.bathrooms + (layout.config.customRooms || []).reduce((s, r) => s + r.count, 0);
            const barWidth = maxCost > 0 ? (layout.cost / maxCost) * 100 : 0;
            const isRenaming = renamingId === layout.id;

            return (
              <div
                key={layout.id}
                className={`rounded-xl border-2 p-4 transition-all ${
                  isCheapest
                    ? darkMode ? 'border-emerald-600/60 bg-emerald-900/10' : 'border-emerald-300 bg-emerald-50/50'
                    : darkMode ? 'border-gray-600 bg-gray-700/40' : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                {/* Layout header with inline rename */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                    {isCheapest && !isRenaming && (
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                        Best Value
                      </span>
                    )}
                    {isRenaming ? (
                      <div className="flex-1 flex items-center gap-1.5">
                        <input
                          ref={renameInputRef}
                          type="text"
                          value={renameValue}
                          onChange={(e) => { setRenameValue(e.target.value); setRenameError(''); }}
                          onKeyDown={(e) => handleRenameKeyDown(e, layout.id)}
                          maxLength={30}
                          className={`flex-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                            renameError ? 'border-rose-400' : darkMode ? 'border-teal-500' : 'border-teal-400'
                          } ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} focus:outline-none focus:ring-2 focus:ring-teal-400`}
                        />
                        <button
                          onClick={() => commitRename(layout.id)}
                          className="w-6 h-6 flex items-center justify-center rounded-md bg-teal-500 text-white cursor-pointer hover:bg-teal-600 transition-colors flex-shrink-0"
                        >
                          <i className="ri-check-line text-xs"></i>
                        </button>
                        <button
                          onClick={cancelRename}
                          className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer transition-colors flex-shrink-0 ${darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startRename(layout)}
                        className={`group flex items-center gap-1.5 cursor-pointer text-left min-w-0`}
                        title="Click to rename"
                      >
                        <span className={`text-sm font-bold truncate ${text}`}>{layout.name}</span>
                        <i className={`ri-pencil-line text-xs opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
                      </button>
                    )}
                  </div>
                  {!isRenaming && (
                    <button
                      onClick={() => handleRemoveLayout(layout.id)}
                      className={`w-6 h-6 flex items-center justify-center rounded-md cursor-pointer transition-colors flex-shrink-0 ${darkMode ? 'text-gray-500 hover:text-rose-400 hover:bg-gray-600' : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50'}`}
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                </div>

                {/* Rename error */}
                {isRenaming && renameError && (
                  <p className="text-xs text-rose-500 mb-2 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>{renameError}
                  </p>
                )}

                {/* Room summary pills */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {[
                    { icon: 'ri-sofa-line', val: `${layout.config.livingRooms || 1}`, colorClass: 'text-teal-500' },
                    { icon: 'ri-hotel-bed-line', val: `${layout.config.bedrooms}`, colorClass: 'text-violet-500' },
                    { icon: 'ri-water-flash-line', val: `${layout.config.bathrooms}`, colorClass: 'text-cyan-500' },
                  ].map(p => (
                    <span key={p.icon} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                      <i className={`${p.icon} ${p.colorClass}`}></i>{p.val}
                    </span>
                  ))}
                  {(layout.config.customRooms || []).map(r => (
                    <span key={r.id} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-pink-900/30 text-pink-300' : 'bg-pink-50 text-pink-700 border border-pink-200'}`}>
                      <i className="ri-home-gear-line text-pink-500"></i>
                      {r.count > 1 ? `${r.count}× ` : ''}{r.name}
                    </span>
                  ))}
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                    {totalRooms} rooms
                  </span>
                </div>

                {/* Cost bar */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs ${subtext}`}>Estimated cost</span>
                    <span className={`text-sm font-bold ${TIER_COLORS[layout.tier] || 'text-gray-500'}`}>{fmt(layout.cost)}</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${TIER_BG[layout.tier] || 'bg-gray-400'}`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <p className={`text-xs mt-1 ${subtext}`}>{fmt(layout.costMin)} – {fmt(layout.costMax)} range</p>
                </div>

                {/* Tier + area */}
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    layout.tier === 'Budget' ? darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700' :
                    layout.tier === 'Mid-Range' ? darkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700' :
                    layout.tier === 'Premium' ? darkMode ? 'bg-violet-900/30 text-violet-400' : 'bg-violet-100 text-violet-700' :
                    darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {layout.tier}
                  </span>
                  <span className={`text-xs ${subtext}`}>{layout.config.area} {layout.config.unit}</span>
                </div>
              </div>
            );
          })}

          {/* Cost diff panel */}
          {layouts.length > 1 && cheapest && (
            <div className={`rounded-xl p-3 border ${darkMode ? 'bg-gray-700/40 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-xs font-semibold mb-2 ${subtext}`}>Cost difference vs. cheapest</p>
              {layouts.filter(l => l.id !== cheapest.id).map(l => (
                <div key={l.id} className="flex items-center justify-between mb-1">
                  <span className={`text-xs ${text}`}>{l.name}</span>
                  <span className="text-xs font-bold text-rose-500">+{fmt(l.cost - cheapest.cost)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${darkMode ? 'bg-teal-900/30 text-teal-300' : 'bg-teal-50 text-teal-700'}`}>
          <i className="ri-checkbox-circle-fill text-teal-500"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
